import { useState } from 'react';
import { downloadDocumentPdf, downloadReceiptPdf, printElement } from '../lib/pdf.js';
import { openWhatsApp } from '../lib/whatsapp.js';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { IconDownload, IconPrint, IconWhatsApp } from './Icons.jsx';

/**
 * Share row under a receipt or statement.
 *
 * WhatsApp gets a formatted text version plus a link back to this page -
 * wa.me links cannot carry a file, so attaching the PDF stays a manual step
 * for the person sending it. To make that step as short as possible, the
 * WhatsApp button also triggers the PDF download in the background, so the
 * file is already sitting in Downloads by the time WhatsApp is open.
 */
export function DocumentActions({ targetRef, filename, whatsappText, phone, countryCode = '964', paged = false }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const savePdf = async () => {
    if (!targetRef.current) return;
    setBusy(true);
    try {
      const download = paged ? downloadDocumentPdf : downloadReceiptPdf;
      await download(targetRef.current, filename);
      toast.success(t.doc.pdfSaved);
    } catch (err) {
      toast.error(t.doc.pdfFailed(err.message));
    } finally {
      setBusy(false);
    }
  };

  const sendWhatsApp = () => {
    // openWhatsApp's window.open must run synchronously, in the same tick as
    // this click - savePdf() is async (html2canvas rendering), so WhatsApp
    // opens first and the PDF download follows, rather than risking the
    // popup blocker treating a delayed window.open as unrequested.
    openWhatsApp(whatsappText, phone, countryCode);
    savePdf();
  };

  return (
    <div className="btn-row no-print">
      <button type="button" className="btn btn-primary" onClick={sendWhatsApp}>
        <IconWhatsApp /> {t.doc.sendWhatsApp}
      </button>
      <button type="button" className="btn" onClick={savePdf} disabled={busy}>
        {busy ? <span className="spinner" /> : <IconDownload />} {t.doc.downloadPdf}
      </button>
      <button type="button" className="btn" onClick={() => targetRef.current && printElement(targetRef.current, paged)}>
        <IconPrint /> {t.doc.print}
      </button>
    </div>
  );
}
